import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../axios-instance";

// ─── Default threshold keys for the table ────────────────────────────────────
const THRESHOLD_BUCKETS = [
  { key: "booking_failures",                label: "Booking Failures",                icon: "📅" },
  { key: "stripe_webhook_failures",         label: "Stripe Webhook Failures",         icon: "💳" },
  { key: "calendar_conflict_fetch_failures",label: "Calendar Conflict Fetch Failures",icon: "📆" },
  { key: "calendar_connection_failures",    label: "Calendar Connection Failures",    icon: "🔗" },
  { key: "payment_failures",                label: "Payment Failures",                icon: "💰" },
  { key: "email_notification_failures",     label: "Email Notification Failures",     icon: "✉️" },
  { key: "production_500s",                 label: "Production 500s",                 icon: "🔥" },
  { key: "failed_jobs",                     label: "Failed Jobs",                     icon: "⚙️" },
  { key: "payment_confirmation_mismatch",   label: "Payment Confirmation Mismatch",   icon: "🔍" },
];

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = {
  page:      { padding: "28px 32px", fontFamily: "Inter, Arial, sans-serif", color: "#1e293b", maxWidth: 1100 },
  pageTitle: { fontSize: 22, fontWeight: 700, marginBottom: 4 },
  subtitle:  { color: "#64748b", fontSize: 14, marginBottom: 28 },
  card:      { background: "#fff", borderRadius: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.08)", padding: "24px 28px", marginBottom: 24 },
  cardTitle: { fontSize: 16, fontWeight: 700, marginBottom: 4, display: "flex", alignItems: "center", gap: 8 },
  cardDesc:  { fontSize: 13, color: "#64748b", marginBottom: 18 },
  label:     { display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 5 },
  input:     { width: "100%", border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 12px", fontSize: 14, outline: "none", boxSizing: "border-box" },
  inputSmall:{ width: 90, border: "1px solid #d1d5db", borderRadius: 6, padding: "6px 10px", fontSize: 13, textAlign: "center", boxSizing: "border-box" },
  toggle:    { display: "flex", alignItems: "center", gap: 10 },
  row:       { display: "flex", gap: 20, marginBottom: 18, flexWrap: "wrap" },
  field:     { flex: 1, minWidth: 180 },
  tag:       { display: "inline-flex", alignItems: "center", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 20, padding: "3px 12px", fontSize: 13, gap: 8, marginRight: 6, marginBottom: 6 },
  tagRemove: { background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 16, lineHeight: 1, padding: 0 },
  addRow:    { display: "flex", gap: 8, marginTop: 8 },
  addInput:  { flex: 1, border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 12px", fontSize: 14, outline: "none" },
  addBtn:    { background: "#6366f1", color: "#fff", border: "none", borderRadius: 6, padding: "8px 16px", cursor: "pointer", fontSize: 14, fontWeight: 600 },
  saveBtn:   { background: "#10b981", color: "#fff", border: "none", borderRadius: 8, padding: "12px 32px", cursor: "pointer", fontSize: 15, fontWeight: 700 },
  testBtn:   { background: "#fff", color: "#4f46e5", border: "1.5px solid #6366f1", borderRadius: 6, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  testBtnDisabled: { background: "#f8fafc", color: "#94a3b8", border: "1.5px solid #e2e8f0", borderRadius: 6, padding: "8px 16px", cursor: "not-allowed", fontSize: 13, fontWeight: 600 },
  testResult:{ marginTop: 12, padding: "10px 14px", borderRadius: 6, fontSize: 13 },
  tbl:       { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th:        { background: "#f8fafc", padding: "8px 12px", textAlign: "center", fontWeight: 600, color: "#374151", borderBottom: "2px solid #e2e8f0" },
  thLeft:    { background: "#f8fafc", padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#374151", borderBottom: "2px solid #e2e8f0" },
  td:        { padding: "8px 12px", borderBottom: "1px solid #f1f5f9", textAlign: "center", verticalAlign: "middle" },
  tdLeft:    { padding: "8px 12px", borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" },
  alert:     { padding: "10px 16px", borderRadius: 8, fontSize: 14, marginBottom: 20 },
  levelBadge:{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10, display: "inline-block" },
};

// ─── Toggle Switch ────────────────────────────────────────────────────────────
const Toggle = ({ checked, onChange, label }) => (
  <label
    onClick={() => onChange(!checked)}
    style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none" }}
  >
    <div
      style={{
        width: 44, height: 24, borderRadius: 12,
        background: checked ? "#10b981" : "#d1d5db",
        position: "relative", transition: "background 0.2s", flexShrink: 0,
      }}
    >
      <div style={{
        width: 18, height: 18, borderRadius: 9, background: "#fff",
        position: "absolute", top: 3,
        left: checked ? 23 : 3, transition: "left 0.2s",
        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
      }} />
    </div>
    <span style={{ fontSize: 14, color: "#374151" }}>{label}</span>
  </label>
);

// ─── Tag input (add / remove list items) ────────────────────────────────────
const TagInput = ({ items, onAdd, onRemove, placeholder, type = "text" }) => {
  const [val, setVal] = useState("");
  const list = Array.isArray(items) ? items : [];

  const handleAdd = () => {
    const trimmed = val.trim();
    if (trimmed && !list.includes(trimmed)) {
      onAdd(trimmed);
      setVal("");
    }
  };

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", marginBottom: 6 }}>
        {list.map((item) => (
          <span key={item} style={styles.tag}>
            {item}
            <button style={styles.tagRemove} onClick={() => onRemove(item)} title="Remove">×</button>
          </span>
        ))}
        {list.length === 0 && <span style={{ fontSize: 13, color: "#94a3b8" }}>None added yet</span>}
      </div>
      <div style={styles.addRow}>
        <input
          type={type}
          style={styles.addInput}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAdd())}
          placeholder={placeholder}
        />
        <button style={styles.addBtn} onClick={handleAdd}>+ Add</button>
      </div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
const AlertSettings = () => {
  const [cfg, setCfg]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const [success, setSuccess] = useState(null);
  const [history, setHistory] = useState([]);
  const [histTab, setHistTab] = useState(false);
  const [testing, setTesting] = useState(null); // 'telegram' | 'email' | 'sms' | null
  const [testResult, setTestResult] = useState({}); // { telegram: {ok, message}, ... }

  // ── Fetch ───────────────────────────────────────────────────────────────
  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    axiosInstance
      .get("super-admin/alert-settings")
      .then((res) => {
        const data = res.data.data || res.data || {};
        setCfg({
          ...data,
          telegram_chat_ids: Array.isArray(data.telegram_chat_ids)
            ? data.telegram_chat_ids.map(String)
            : [],
          email_recipients: Array.isArray(data.email_recipients)
            ? data.email_recipients
            : [],
          sms_recipients: Array.isArray(data.sms_recipients)
            ? data.sms_recipients.map(String)
            : [],
          thresholds: data.thresholds && typeof data.thresholds === "object"
            ? data.thresholds
            : {},
        });
      })
      .catch(() => setError("Failed to load alert configuration."))
      .finally(() => setLoading(false));
  }, []);

  const loadHistory = useCallback(() => {
    axiosInstance
      .get("super-admin/alert-settings/history")
      .then((res) => {
        // ResponseBuilder wraps payloads in { data: { ... } }. Prefer the
        // keyed list; never fall back to the whole response object (not an array).
        const payload = res?.data?.data;
        const list = Array.isArray(payload?.events)
          ? payload.events
          : Array.isArray(payload)
            ? payload
            : [];
        setHistory(list);
      })
      .catch(() => setHistory([]));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (histTab) loadHistory(); }, [histTab, loadHistory]);

  // ── Helpers ─────────────────────────────────────────────────────────────
  const set = (key, value) => setCfg((prev) => ({ ...prev, [key]: value }));

  const setThreshold = (bucket, field, value) => {
    // Allow empty string while typing; clamp to ≥1 on anything else so a
    // cleared/zero field cannot be saved as 0 (which would cause non-stop alerts).
    const parsed = value === "" ? "" : Math.max(1, parseInt(value, 10) || 1);
    setCfg((prev) => ({
      ...prev,
      thresholds: {
        ...prev.thresholds,
        [bucket]: {
          ...(prev.thresholds || {})[bucket],
          [field]: parsed,
        },
      },
    }));
  };

  const addTag = (key, value) =>
    set(key, [...(Array.isArray(cfg[key]) ? cfg[key] : []), value]);
  const removeTag = (key, value) =>
    set(key, (Array.isArray(cfg[key]) ? cfg[key] : []).filter((v) => v !== value));

  // ── Save ─────────────────────────────────────────────────────────────────
  const save = () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    axiosInstance
      .post("super-admin/alert-settings", cfg)
      .then(() => {
        setSuccess("Configuration saved successfully.");
        setTimeout(() => setSuccess(null), 4000);
      })
      .catch((err) => setError(err?.response?.data?.message || "Failed to save configuration."))
      .finally(() => setSaving(false));
  };

  // ── Test channel ─────────────────────────────────────────────────────────
  const extractErrorMessage = (err) => {
    const data = err?.response?.data;
    if (!data) return "Test failed. Check credentials and try again.";

    // Prefer explicit API message
    let message = data.message || data?.data?.message || "";

    // Flatten Laravel / ResponseBuilder validation errors
    const errors = data.errors || data?.data?.messages || null;
    if (errors && typeof errors === "object" && !Array.isArray(errors)) {
      const parts = Object.values(errors)
        .flat()
        .filter(Boolean)
        .map(String);
      if (parts.length) {
        message = parts.join(" ");
      }
    }

    if (!message || message === "The given data was invalid.") {
      message = "Test failed. Check credentials and try again.";
    }
    return message;
  };

  const testChannel = (channel) => {
    setTesting(channel);
    setTestResult((prev) => ({ ...prev, [channel]: null }));

    const payload = { channel };
    if (channel === "telegram") {
      payload.telegram_bot_token = cfg.telegram_bot_token || "";
      payload.telegram_chat_ids = (cfg.telegram_chat_ids || []).map(String);
    } else if (channel === "email") {
      payload.email_recipients = cfg.email_recipients || [];
    } else if (channel === "sms") {
      payload.sms_recipients = (cfg.sms_recipients || []).map(String);
    }

    axiosInstance
      .post("super-admin/alert-settings/test", payload)
      .then((res) => {
        const data = res?.data?.data || {};
        const message =
          data.message ||
          res?.data?.message ||
          "Test sent successfully.";
        setTestResult((prev) => ({ ...prev, [channel]: { ok: true, message } }));
      })
      .catch((err) => {
        setTestResult((prev) => ({
          ...prev,
          [channel]: { ok: false, message: extractErrorMessage(err) },
        }));
      })
      .finally(() => setTesting(null));
  };

  const renderTestRow = (channel, label) => {
    const result = testResult[channel];
    const busy = testing === channel;
    return (
      <div style={{ marginTop: 18 }}>
        <button
          type="button"
          style={busy ? styles.testBtnDisabled : styles.testBtn}
          disabled={!!testing}
          onClick={() => testChannel(channel)}
        >
          {busy ? `Sending ${label} test…` : `Send test ${label}`}
        </button>
        <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 6 }}>
          Uses the values currently entered above (save is not required first).
        </div>
        {result && (
          <div
            style={{
              ...styles.testResult,
              background: result.ok ? "#dcfce7" : "#fee2e2",
              color: result.ok ? "#166534" : "#991b1b",
            }}
          >
            {result.ok ? "✓ " : "✗ "}{result.message}
          </div>
        )}
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={styles.page}>
        <div style={{ color: "#64748b", fontSize: 15 }}>Loading alert configuration…</div>
      </div>
    );
  }

  if (!cfg) {
    return (
      <div style={styles.page}>
        <div style={{ color: "#ef4444", fontSize: 15 }}>{error || "Unable to load configuration."}</div>
      </div>
    );
  }

  const thresholds = cfg.thresholds || {};

  const levelColors = { 1: "#d97706", 2: "#ea580c", 3: "#dc2626" };

  return (
    <div style={styles.page}>
      <div style={styles.pageTitle}>🚨 P0 Launch Alert Settings</div>
      <div style={styles.subtitle}>
        Configure Telegram, Email, SMS channels and thresholds for P0 launch-failure alerts.
      </div>

      {/* Tab bar */}
      <div style={{ display: "flex", gap: 4, marginBottom: 24 }}>
        {["Settings", "Alert History"].map((tab, i) => (
          <button
            key={tab}
            onClick={() => setHistTab(i === 1)}
            style={{
              padding: "8px 20px", borderRadius: 6, border: "none", cursor: "pointer",
              fontWeight: 600, fontSize: 14,
              background: (i === 1) === histTab ? "#6366f1" : "#f1f5f9",
              color:      (i === 1) === histTab ? "#fff"    : "#374151",
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* ── HISTORY TAB ─────────────────────────────────────────────────── */}
      {histTab && (
        <div style={styles.card}>
          <div style={styles.cardTitle}>📋 Recent Alert History</div>
          <div style={styles.cardDesc}>Last 100 fired alerts with channel breakdown.</div>
          {!Array.isArray(history) || history.length === 0 ? (
            <div style={{ color: "#64748b", fontSize: 14 }}>No alerts have been fired yet.</div>
          ) : (
            <table style={styles.tbl}>
              <thead>
                <tr>
                  <th style={styles.thLeft}>Bucket</th>
                  <th style={styles.th}>Level</th>
                  <th style={styles.th}>Count</th>
                  <th style={styles.th}>Channels</th>
                  <th style={styles.th}>Fired At</th>
                </tr>
              </thead>
              <tbody>
                {history.map((row) => (
                  <tr key={row.id}>
                    <td style={styles.tdLeft}>{row.bucket}</td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.levelBadge,
                        background: (levelColors[row.level] || "#9ca3af") + "22",
                        color: levelColors[row.level] || "#9ca3af",
                      }}>
                        {row.level === 1 ? "⚠ WARNING" : row.level === 2 ? "🔴 CRITICAL" : "🚨 SEVERE"}
                      </span>
                    </td>
                    <td style={styles.td}>{row.failure_count}</td>
                    <td style={styles.td}>
                      {(Array.isArray(row.channels) ? row.channels : []).join(", ") || "—"}
                    </td>
                    <td style={styles.td}>{row.fired_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── SETTINGS TAB ────────────────────────────────────────────────── */}
      {!histTab && (
        <>
          {error   && <div style={{ ...styles.alert, background: "#fee2e2", color: "#991b1b" }}>{error}</div>}
          {success && <div style={{ ...styles.alert, background: "#dcfce7", color: "#166534" }}>✓ {success}</div>}

          {/* ── 1. General ──────────────────────────────────────────────── */}
          <div style={styles.card}>
            <div style={styles.cardTitle}>⚙️ General</div>
            <div style={styles.cardDesc}>Master switch and per-level cooldowns.</div>

            <div style={{ marginBottom: 20 }}>
              <Toggle
                checked={cfg.alert_enabled !== false}
                onChange={(v) => set("alert_enabled", v)}
                label="Alerts enabled"
              />
            </div>

            <div style={styles.row}>
              <div style={styles.field}>
                <label style={styles.label}>Environment guard</label>
                <input
                  style={styles.input}
                  type="text"
                  placeholder="e.g. production  (leave blank for all)"
                  value={cfg.environment || ""}
                  onChange={(e) => set("environment", e.target.value)}
                />
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
                  Level 2 &amp; 3 alerts are skipped when the current environment does not match this value.
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              {[
                { key: "cooldown_warning",  label: "🟡 Warning cooldown (min)",  tip: "Minimum minutes between repeated Warning alerts for the same bucket." },
                { key: "cooldown_critical", label: "🔴 Critical cooldown (min)", tip: "Minimum minutes between repeated Critical alerts for the same bucket." },
                { key: "cooldown_severe",   label: "🚨 Severe cooldown (min)",   tip: "Minimum minutes between repeated Severe alerts for the same bucket." },
              ].map(({ key, label, tip }) => (
                <div key={key} style={{ minWidth: 180 }}>
                  <label style={styles.label}>{label}</label>
                  <input
                    style={{ ...styles.input, width: 100 }}
                    type="number"
                    min={1}
                    value={cfg[key] ?? ""}
                    onChange={(e) => set(key, parseInt(e.target.value, 10) || 1)}
                  />
                  <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4, maxWidth: 200 }}>{tip}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ── 2. Telegram ─────────────────────────────────────────────── */}
          <div style={styles.card}>
            <div style={styles.cardTitle}>📱 Telegram <span style={{ fontSize: 12, color: "#64748b", fontWeight: 400 }}>— Level 1, 2, 3</span></div>
            <div style={styles.cardDesc}>
              Instant free mobile notifications. Create a bot via @BotFather, add it to your channel/group, and use that channel's chat ID.
            </div>

            <div style={{ marginBottom: 20 }}>
              <Toggle
                checked={cfg.telegram_enabled !== false}
                onChange={(v) => set("telegram_enabled", v)}
                label="Telegram enabled"
              />
            </div>

            <div style={{ ...styles.row, marginBottom: 20 }}>
              <div style={{ flex: 1, minWidth: 260 }}>
                <label style={styles.label}>Bot Token</label>
                <input
                  style={styles.input}
                  type="password"
                  placeholder="123456789:ABCdefGhIJK..."
                  value={cfg.telegram_bot_token || ""}
                  onChange={(e) => set("telegram_bot_token", e.target.value)}
                  autoComplete="new-password"
                />
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
                  From @BotFather → /newbot → copy the token
                </div>
              </div>
            </div>

            <div>
              <label style={styles.label}>Chat IDs <span style={{ fontWeight: 400, color: "#94a3b8" }}>(channel, group, or user)</span></label>
              <TagInput
                items={cfg.telegram_chat_ids || []}
                onAdd={(v) => addTag("telegram_chat_ids", v)}
                onRemove={(v) => removeTag("telegram_chat_ids", v)}
                placeholder="-1001234567890"
              />
              <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 6 }}>
                For a private channel: forward a message from it to @getidsbot to find its ID. Prefix with <code>-100</code> for supergroups/channels.
              </div>
            </div>
            {renderTestRow("telegram", "Telegram")}
          </div>

          {/* ── 3. Email ─────────────────────────────────────────────────── */}
          <div style={styles.card}>
            <div style={styles.cardTitle}>✉️ Email <span style={{ fontSize: 12, color: "#64748b", fontWeight: 400 }}>— Level 2 + 3</span></div>
            <div style={styles.cardDesc}>
              Permanent audit trail, searchable, management can be CC'd. Sent on Critical and Severe alerts only.
            </div>

            <div style={{ marginBottom: 20 }}>
              <Toggle
                checked={cfg.email_enabled !== false}
                onChange={(v) => set("email_enabled", v)}
                label="Email enabled"
              />
            </div>

            <label style={styles.label}>Recipient email addresses</label>
            <TagInput
              items={cfg.email_recipients || []}
              onAdd={(v) => addTag("email_recipients", v)}
              onRemove={(v) => removeTag("email_recipients", v)}
              placeholder="ops@yourcompany.com"
              type="email"
            />
            {renderTestRow("email", "Email")}
          </div>

          {/* ── 4. SMS ──────────────────────────────────────────────────── */}
          <div style={styles.card}>
            <div style={styles.cardTitle}>📟 SMS (Twilio) <span style={{ fontSize: 12, color: "#64748b", fontWeight: 400 }}>— Level 3 only</span></div>
            <div style={styles.cardDesc}>
              Reserved for true P0 incidents: production down, booking completely broken, Stripe completely broken.
              Requires <code>TWILIO_SID</code>, <code>TWILIO_AUTH_TOKEN</code>, and <code>TWILIO_NUMBER</code> to be set in your .env file.
            </div>

            <div style={{ marginBottom: 20 }}>
              <Toggle
                checked={cfg.sms_enabled !== false}
                onChange={(v) => set("sms_enabled", v)}
                label="SMS enabled"
              />
            </div>

            <label style={styles.label}>Recipient phone numbers <span style={{ fontWeight: 400, color: "#94a3b8" }}>(E.164 format)</span></label>
            <TagInput
              items={cfg.sms_recipients || []}
              onAdd={(v) => addTag("sms_recipients", v)}
              onRemove={(v) => removeTag("sms_recipients", v)}
              placeholder="+1234567890"
            />
            {renderTestRow("sms", "SMS")}
          </div>

          {/* ── 5. Thresholds ───────────────────────────────────────────── */}
          <div style={styles.card}>
            <div style={styles.cardTitle}>🎚️ Alert Thresholds</div>
            <div style={styles.cardDesc}>
              Number of failures that must occur within the look-back window to trigger each alert level.
            </div>

            <div style={{ overflowX: "auto" }}>
              <table style={styles.tbl}>
                <thead>
                  <tr>
                    <th style={{ ...styles.thLeft, minWidth: 210 }}>Bucket</th>
                    <th style={{ ...styles.th, background: "#fffbeb", color: "#92400e" }}>🟡 Warning count</th>
                    <th style={{ ...styles.th, background: "#fffbeb", color: "#92400e" }}>🟡 Warning window (min)</th>
                    <th style={{ ...styles.th, background: "#fff7ed", color: "#9a3412" }}>🔴 Critical count</th>
                    <th style={{ ...styles.th, background: "#fff7ed", color: "#9a3412" }}>🔴 Critical window (min)</th>
                    <th style={{ ...styles.th, background: "#fef2f2", color: "#991b1b" }}>🚨 Severe count</th>
                    <th style={{ ...styles.th, background: "#fef2f2", color: "#991b1b" }}>🚨 Severe window (min)</th>
                  </tr>
                </thead>
                <tbody>
                  {THRESHOLD_BUCKETS.map(({ key, label, icon }) => {
                    const row = thresholds[key] || {};
                    return (
                      <tr key={key}>
                        <td style={styles.tdLeft}>
                          <span style={{ marginRight: 6 }}>{icon}</span>
                          <span style={{ fontWeight: 600 }}>{label}</span>
                        </td>
                        {[
                          ["warning_count",  "#fffbeb"],
                          ["warning_window", "#fffbeb"],
                          ["critical_count", "#fff7ed"],
                          ["critical_window","#fff7ed"],
                          ["severe_count",   "#fef2f2"],
                          ["severe_window",  "#fef2f2"],
                        ].map(([field, bg]) => (
                          <td key={field} style={{ ...styles.td, background: bg }}>
                            <input
                              style={styles.inputSmall}
                              type="number"
                              min={1}
                              value={row[field] ?? ""}
                              onChange={(e) => setThreshold(key, field, e.target.value)}
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 10 }}>
              All values must be ≥ 1. Clearing a field and saving will reset it to the coded default on next load.
            </div>
          </div>

          {/* ── Save button ─────────────────────────────────────────────── */}
          <div style={{ textAlign: "right", paddingBottom: 40 }}>
            <button style={styles.saveBtn} onClick={save} disabled={saving}>
              {saving ? "Saving…" : "💾  Save Configuration"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AlertSettings;
